import {
  Button,
  Checkbox,
  Icon,
  Section,
  SectionCard,
} from "@snpjs/core";

import { Dialog } from "../../../components/dialog/Dialog";
import { DetailList } from "../../../components/detailList/DetailList";
import { ISystemLandscapeProps } from "./ISystemLandscapeProps";
import { InputWithEditButton } from "../../../components/inputWithEditButton/InputWithEditButton";
import { MessageArea } from "../../../components/messageArea/MessageArea";
import { ServicePackVersionDetails } from "../../versionDetails/servicePackVersionDetails/ServicePackVersionDetails";
import { Spinner } from "../../../components/spinner/Spinner";
import { SystemSection } from "../systemSection/SystemSection";
import { TagElement } from "../../../components/tagElement/TagElement";
import styles from "./SystemLandscape.module.scss";
import { texts } from "../../../i18n/texts";
import { useSetSystemLandscapeDownloadVersionCommand } from "../systemCommands/useSetSystemLandscapeDownloadVersionCommand";
import { useSetSystemLandscapeUpgradeVersionCommand } from "../systemCommands/useSetSystemLandscapeUpgradeVersionCommand";
import { useState } from "react";
import useSystemLandscapeViewModel from "./useSystemLandscapeViewModel";
import useTranslation from "../../../hooks/useTranslation";

export const SystemLandscape: React.FC<ISystemLandscapeProps> = (props) => {
  const viewModel = useSystemLandscapeViewModel(props);
  const { t } = useTranslation();
  const [downloadVersion, setDownloadVersion] = useState(false);
  const [upgradeVersion, setUpgradeVersion] = useState(false);
  const [isPasteInformationDialogOpen, setPasteInformationDialogOpen] =
    useState(false);
  const [pendingPasteLandscapeId, setPendingPasteLandscapeId] = useState<
    string | undefined
  >();

  const handleDownloadVersion = () => {
    setDownloadVersion(!downloadVersion);
  };

  const handleUpgradeVersion = () => {
    setUpgradeVersion(!upgradeVersion);
  };

  const downloadVersionCommand = useSetSystemLandscapeDownloadVersionCommand({
    productId: props.product.OBJECT_ID,
    isVersionChangeable: true,
    systemLandscape: props.systemLandscape,
  });

  const upgradeVersionCommand = useSetSystemLandscapeUpgradeVersionCommand({
    productId: props.product.OBJECT_ID,
    isVersionChangeable: true,
    systemLandscape: props.systemLandscape,
    release: props.systemLandscape.RELEASE,
    isLTS: props.systemLandscape.IS_LTS,
    servicePack: props.systemLandscape.SERVICE_PACK,
    hotfix: props.systemLandscape.HOTFIX,
  });

  const systemsForSelection = [
    ...(props.systems ?? []),
    ...props.pastedSystemsForLandscape,
  ];
  const landscapeId = viewModel.systemLandscape.OBJECT_ID;
  const systemSelectionKeysForLandscape = systemsForSelection.map(
    (system, index) => {
      const number = index + 1;
      const systemDraftKey =
        system.OBJECT_ID || `draft-${landscapeId}-${number}`;
      return `${landscapeId}:${system.OBJECT_ID || systemDraftKey}`;
    }
  );
  const allSystemsInLandscapeSelected =
    systemSelectionKeysForLandscape.length > 0 &&
    systemSelectionKeysForLandscape.every((key) =>
      props.isSystemSelected(key)
    );

  const title = (
    <div className={styles.title}>
      {props.enableSystemLandscapeCopyPaste && (
        <Checkbox
          aria-label={t(
            texts.systemLandscapeHeader.selectAllSystemsInLandscape
          )}
          checked={allSystemsInLandscapeSelected}
          disabled={systemsForSelection.length === 0}
          onChange={(event) =>
            props.onToggleAllSystemsInLandscape(
              landscapeId,
              event.target.checked,
              props.systems ?? [],
              props.pastedSystemsForLandscape
            )
          }
        />
      )}
      <Icon icon="hard-drives" className={styles.titleIcon} />
      <InputWithEditButton
        initialValue={
          viewModel.systemLandscape.DESCRIPTION ??
          t(texts.systemLandscape.defaultSystemLandscapeName)
        }
        onEdit={async (description) =>
          await viewModel.saveSystemLandscapeDescription(description)
        }
        widthInRem={15}
      />
      {viewModel.showDeleteButton &&
        (viewModel.showDeleteButtonLoadingSpinner ? (
          <Spinner />
        ) : (
          <Icon
            icon={"trash"}
            className={styles.deleteIcon}
            onClick={viewModel.deleteLandscape}
          />
        ))}
    </div>
  );

  const handlePasteClick = () => {
    if (props.clipboardPasteSystemsCount === 0) {
      void props.onPasteClipboardSystems(props.systemLandscape.OBJECT_ID);
      return;
    }
    setPendingPasteLandscapeId(props.systemLandscape.OBJECT_ID);
    setPasteInformationDialogOpen(true);
  };

  const closePasteInformationDialog = () => {
    setPasteInformationDialogOpen(false);
    setPendingPasteLandscapeId(undefined);
  };

  const handlePasteInformationDialogOk = async () => {
    if (pendingPasteLandscapeId) {
      await props.onPasteClipboardSystems(pendingPasteLandscapeId);
    }
    closePasteInformationDialog();
  };

  return (
    <>
    <Section
      title={title}
      className={styles.systemLandscape}
      rightElement={
        <div className={styles.systemLandscapeButtonContainer}>
          {props.enableSystemLandscapeCopyPaste && (
            <Button
              icon="clipboard-text"
              intent="none"
              text={`${t(texts.systemLandscapeHeader.pasteSystems)} (${props.clipboardPasteSystemsCount})`}
              onClick={handlePasteClick}
            />

          )}
          <TagElement
            text={
              t(texts.systemLandscapeHeader.systems) + ": " + props.systems.length
            }
          />
        </div>
      }
    >
      <SectionCard>
        <div>
          {downloadVersionCommand.view}
          {upgradeVersionCommand.view}
          {viewModel.versionCommand.view}
          {viewModel.message && (
            <MessageArea
              className={styles.messageArea}
              message={viewModel.message}
            />
          )}
          <div className={styles.detailList}>
            <DetailList>
              <ServicePackVersionDetails
                servicePackVersion={props.systemLandscape}
              />
            </DetailList>
            {viewModel.canVersionBeAdjusted &&
              props.systemLandscape.RELEASE.toString() !== "0000" && (
                <Button
                  text={t(texts.systemLandscape.captionAdjustVersion)}
                  onClick={() => downloadVersionCommand.execute()}
                  className={styles.systemLandscapeButton}
                />
              )}

            {!viewModel.canVersionBeAdjusted && viewModel.canBeUpgraded && (
              <Button
                text={t(texts.systemLandscape.captionUpgradeVersion)}
                onClick={() => upgradeVersionCommand.execute()}
                className={styles.systemLandscapeButton}
              />
            )}
          </div>
        </div>
      </SectionCard>
      <SectionCard>
        <SystemSection
          onDownloadVersion={handleDownloadVersion}
          onUpgradeVersion={handleUpgradeVersion}
          onSystemSaveDone={props.onSystemSaveDone}
          collapseSignal={props.collapseSignal}
          expandSignal={props.expandSignal}
          onCollapseSingleSystem={props.onCollapseSingleSystem}
          onExpandSingleSystem={props.onExpandSingleSystem}
          containsSystemVersionInconsistency={
            viewModel.containsSystemVersionInconsistency
          }
          contract={props.contract}
          product={props.product}
          searchSignal={props.searchSignal}
          systemLandscape={viewModel.systemLandscape}
          canVersionBeAdjusted={viewModel.canVersionBeAdjusted}
          canBeUpgraded={viewModel.canBeUpgraded}
          systems={props.systems ?? []}
          pastedSystemsForLandscape={props.pastedSystemsForLandscape}
          allSystemsInProduct={props.allSystemsInProduct ?? []}
          systemsDraftCountsBySystemKey={props.systemsDraftCountsBySystemKey}
          onDraftCountsChange={props.onDraftCountsChange}
          systemLandscapeConflicts={props.systemLandscapeConflicts}
          systemRequirementTypes={props.systemRequirementTypes}
          systemRoles={props.systemRoles}
          enableSystemLandscapeCopyPaste={props.enableSystemLandscapeCopyPaste}
          isSystemSelected={props.isSystemSelected}
          onToggleSystemSelection={props.onToggleSystemSelection}
          onSystemDeletedForClipboardCleanup={
            props.onSystemDeletedForClipboardCleanup
          }
        />
      </SectionCard>
    </Section>
    <Dialog
      isOpen={isPasteInformationDialogOpen}
      title={t(texts.systemLandscapeHeader.pasteSystemsInformationTitle)}
      onCancel={closePasteInformationDialog}
      footer={
        <Button onClick={handlePasteInformationDialogOk} intent="primary">
          {t(texts.general.okay)}
        </Button>
      }
    >
      <p className={styles.pasteInformationDialogMessage}>
        {t(texts.systemLandscapeHeader.pasteSystemsInformationMessage)}
      </p>
    </Dialog>
    </>
  );
};
